import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getTenant, getTenantFromPath } from '../utils/tenantUtils';

// Environment-aware debug logging
const isDevelopment = process.env.NODE_ENV === 'development';

if (isDevelopment) {
  console.log('[axiosInstance.js] Development mode - API Base URL:', process.env.REACT_APP_API_BASE_URL);
}

// Throttling variables to prevent refresh loops
let isRefreshing = false;
let refreshSubscribers = [];
let lastRedirectTime = 0;

// Function to notify subscribers when a token has been refreshed
const onRefreshed = () => {
  refreshSubscribers.forEach(callback => callback());
  refreshSubscribers = [];
};

// Function to subscribe to the token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to redirect to login
const redirectToLogin = () => {
  // Prevent rapid redirects
  const now = Date.now();
  if (now - lastRedirectTime < 5000) {
    return;
  }
  
  // Check if already on login page
  if (window.location.pathname === '/login') {
    return;
  }
  
  // Set redirect flag
  lastRedirectTime = now;
  localStorage.setItem('redirected', 'true');
  
  // Dispatch event for other components
  window.dispatchEvent(new CustomEvent('authExpired'));
  
  // Navigate to login - for path-based routing, keep the tenant in the path
  const tenant = getTenant();
  if (tenant) {
    window.location.href = `/$/${tenant}/login`;
  } else {
    window.location.href = '/login';
  }
};

// Create axios instance with dynamic base URL based on tenant
const getBaseUrl = () => {
  const tenant = getTenant();
  const baseApiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
  
  // Debug logging only in development
  if (isDevelopment) {
    console.log('[axiosInstance.js] DEBUG: getTenant() returned:', tenant);
    console.log('[axiosInstance.js] DEBUG: process.env.REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    console.log('[axiosInstance.js] DEBUG: window.location.pathname:', window.location.pathname);
    console.log('[axiosInstance.js] DEBUG: window.location.href:', window.location.href);
  }
  
  if (tenant) {
    // For path-based routing: https://domain.com/$/tenant/api
    // Remove /api from the end and add tenant path
    const baseWithoutApi = baseApiUrl.replace(/\/api$/, '');
    const tenantUrl = `${baseWithoutApi}/$/${tenant}/api`;
    if (isDevelopment) {
      console.log('[axiosInstance.js] DEBUG: Tenant URL calculated:', tenantUrl);
    }
    return tenantUrl;
  }
  
  if (isDevelopment) {
    console.log('[axiosInstance.js] DEBUG: No tenant, using base URL:', baseApiUrl);
  }
  return baseApiUrl;
};

// Function to refresh the token
const refreshToken = async () => {
  // Prevent concurrent refresh attempts
  if (isRefreshing) {
    return new Promise(resolve => {
      subscribeTokenRefresh(() => resolve());
    });
  }
  
  isRefreshing = true;
  
  try {
    // Get tenant-specific base URL without /api suffix
    const tenant = getTenant();
    const baseApiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const baseUrl = tenant
      ? `${baseApiUrl.replace(/\/api$/, '')}/$/${tenant}`
      : baseApiUrl.replace(/\/api$/, '');
      
    // Perform cookie-based refresh
    const cookieResponse = await axios.post(`${baseUrl}/api/token/refresh/cookie/`, {}, {
      withCredentials: true
    });
    
    if (cookieResponse.status === 200) {
      onRefreshed();
      isRefreshing = false;
      return true;
    }
    
    throw new Error('Token refresh failed');
  } catch (error) {
    isRefreshing = false;
    throw error;
  }
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  timeout: 300000, // Increased to 180 seconds (3 minutes) for Render free tier performance
  withCredentials: true
});

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip auth handling for auth-related endpoints
    const isAuthEndpoint = config.url && (
      config.url.includes('/login/') ||
      config.url.includes('/token/') ||
      config.url.includes('/register/') ||
      config.url.includes('/auth/')  // Add this line to include /auth/status/
    );
    
    if (isAuthEndpoint) {
      return config;
    }
    
    // Configure baseURL with tenant path if needed
    const tenant = getTenant();
    const baseApiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const baseWithoutApi = baseApiUrl.replace(/\/api$/, '');
    
    const expectedBaseURL = tenant 
      ? `${baseWithoutApi}/$/${tenant}/api`
      : baseApiUrl;

    if (config.baseURL !== expectedBaseURL) {
      config.baseURL = expectedBaseURL;
    }
    
    // Ensure proper URL construction
    const finalUrl = config.baseURL + (config.url.startsWith('/') ? config.url : '/' + config.url);
    if (isDevelopment) {
      console.log('[axiosInstance.js] DEBUG: Final request URL:', finalUrl);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - only enable detailed logging in development
if (isDevelopment) {
  console.log('[axiosInstance] Response interceptor enabled for development debugging');
}

axiosInstance.interceptors.response.use(
  (response) => {
    if (isDevelopment) {
      console.log('🟢 [axiosInstance] SUCCESS RESPONSE:', {
        status: response.status,
        url: response.config.url,
        dataSize: response.data ? JSON.stringify(response.data).length : 0,
        headers: response.headers['content-type']
      });
    }
    return response;
  },
  async (error) => {
    if (isDevelopment) {
      console.log('🔴 [axiosInstance] ERROR RESPONSE:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message,
        code: error.code,
        name: error.name
      });
    }
    
    // For debugging - just pass through the original error without modification
    return Promise.reject(error);
  }
);

// Logout function
export const logout = async () => {
  try {
    // Get tenant-specific base URL
    const tenant = getTenant();
    const baseApiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const baseUrl = tenant
      ? `${baseApiUrl.replace(/\/api$/, '')}/$/${tenant}`
      : baseApiUrl.replace(/\/api$/, '');
    
    try {
      // Call the logout endpoint - we don't care if this fails
      await axios.post(`${baseUrl}/api/logout/`, {}, { 
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
    } catch (error) {
      // Log but continue - we want to clear local state even if API call fails
    }
  
    // Clear any local storage items related to auth
    localStorage.removeItem('redirected');
    localStorage.removeItem('lastRedirectTime');
    
    // Clear any other tenant-specific items from localStorage
    const tenantPrefix = tenant ? `${tenant}_` : '';
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(tenantPrefix)) {
        localStorage.removeItem(key);
      }
    });
  
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('authExpired'));
    
    return true;  // Always return true as we've cleared local state
  } catch (error) {
    return false;
  }
};

// Login function
export const login = async (credentials) => {
  try {
    // Get tenant-specific base URL
    const tenant = getTenant();
    const baseApiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const baseUrl = tenant
      ? `${baseApiUrl.replace(/\/api$/, '')}/$/${tenant}`
      : baseApiUrl.replace(/\/api$/, '');
      
    // Perform cookie-based login
    const response = await axios.post(`${baseUrl}/api/token/cookie/`, credentials, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    });
    
    // Return response data for backward compatibility with components that expect it
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default axiosInstance;